import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  User,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Check,
  Users,
  Dumbbell,
} from "lucide-react";
import { API } from "@/App";
import { format, addDays, startOfWeek, isSameDay, isToday } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

// Time slots for the day
const TIME_SLOTS = [
  "07:00", "08:00", "09:00", "10:00", "11:00", "12:00",
  "13:00", "14:00", "15:00", "16:00", "17:00", "18:00",
  "19:00", "20:00", "21:00"
];

export default function MemberReservationCalendar({ user, memberPackage }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Get week days starting from today
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(new Date(), i));
    }
    return days;
  };

  const [weekDays] = useState(getWeekDays());

  useEffect(() => {
    if (memberPackage?.business_id) {
      fetchData();
    }
  }, [memberPackage, selectedDate]);

  const fetchData = async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const [trainersRes, roomsRes, reservationsRes] = await Promise.all([
        fetch(`${API}/businesses/${memberPackage.business_id}/trainers`, {
          credentials: "include",
        }),
        fetch(`${API}/businesses/${memberPackage.business_id}/rooms`, {
          credentials: "include",
        }),
        fetch(`${API}/businesses/${memberPackage.business_id}/reservations?date=${dateStr}`, {
          credentials: "include",
        }),
      ]);

      if (trainersRes.ok) setTrainers(await trainersRes.json());
      if (roomsRes.ok) setRooms(await roomsRes.json());
      if (reservationsRes.ok) setReservations(await reservationsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSlotStatus = (time, trainerId, roomId) => {
    const slot = reservations.find(
      (r) =>
        r.time === time &&
        r.trainer_id === trainerId &&
        r.room_id === roomId &&
        ["pending", "confirmed"].includes(r.status)
    );

    if (slot) {
      if (slot.training_type === "group") {
        // Check capacity for group classes
        const room = rooms.find((r) => r.room_id === roomId);
        const groupReservations = reservations.filter(
          (r) =>
            r.time === time &&
            r.room_id === roomId &&
            ["pending", "confirmed"].includes(r.status)
        );
        const remaining = (room?.capacity || 10) - groupReservations.length;
        return { status: "group", remaining };
      }
      return { status: "booked" };
    }
    return { status: "available" };
  };

  const handleTimeSelect = (time) => {
    if (!selectedTrainer) {
      toast.error(t("common:toast_onceBirAntrenorSecin"));
      return;
    }

    // Find available room for this time
    const availableRoom = rooms.find((room) => {
      const slotStatus = getSlotStatus(time, selectedTrainer.trainer_id, room.room_id);
      return slotStatus.status === "available" || slotStatus.status === "group";
    });

    if (!availableRoom) {
      toast.error(t("common:toast_buSaatteMusaitOdaBulunmuyor"));
      return;
    }

    setSelectedTime(time);
    setSelectedRoom(availableRoom);
    setShowConfirmModal(true);
  };

  const handleConfirmReservation = async () => {
    if (!selectedTrainer || !selectedTime || !selectedRoom) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/reservations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          member_package_id: memberPackage.member_package_id,
          trainer_id: selectedTrainer.trainer_id,
          room_id: selectedRoom.room_id,
          date: format(selectedDate, "yyyy-MM-dd"),
          time: selectedTime,
          training_type: "one_on_one",
          branch: selectedTrainer.branches?.[0] || "Pilates",
        }),
      });

      if (response.ok) {
        toast.success(t("common:reservationRequestReceived"));
        setShowConfirmModal(false);
        setSelectedTime(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:reservationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-50 pt-4 pb-2">
        <h2 className="font-heading font-bold text-xl text-navy mb-1">
          Rezervasyon Yap
        </h2>
        <p className="text-grey text-sm">
          Kalan Seans: <span className="font-semibold text-navy">{memberPackage?.remaining_sessions || 0}</span>
        </p>
      </div>

      {/* Date Selector - Horizontal Scroll */}
      <div className="overflow-x-auto pb-2 -mx-4 px-4">
        <div className="flex gap-2 min-w-max">
          {weekDays.map((day) => (
            <button
              key={day.toISOString()}
              onClick={() => setSelectedDate(day)}
              className={`flex flex-col items-center p-3 rounded-xl min-w-[70px] transition-all ${
                isSameDay(day, selectedDate)
                  ? "bg-navy text-white shadow-lg"
                  : isToday(day)
                  ? "bg-red-50 text-red-600 border-2 border-red-200"
                  : "bg-white text-grey-700 border border-grey-200"
              }`}
              data-testid={`date-${format(day, "yyyy-MM-dd")}`}
            >
              <span className="text-xs font-medium uppercase">
                {format(day, "EEE", { locale: getDateLocale() })}
              </span>
              <span className="text-2xl font-bold mt-1">
                {format(day, "d")}
              </span>
              <span className="text-xs mt-1">
                {format(day, "MMM", { locale: getDateLocale() })}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Trainer Selection */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <User className="w-4 h-4" />
            Antrenör Seçin
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {trainers.length > 0 ? (
              trainers.map((trainer) => (
                <button
                  key={trainer.trainer_id}
                  onClick={() => setSelectedTrainer(trainer)}
                  className={`p-3 rounded-xl flex items-center gap-3 transition-all ${
                    selectedTrainer?.trainer_id === trainer.trainer_id
                      ? "bg-navy text-white"
                      : "bg-slate-50 hover:bg-slate-100"
                  }`}
                  data-testid={`trainer-${trainer.trainer_id}`}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback
                      className={
                        selectedTrainer?.trainer_id === trainer.trainer_id
                          ? "bg-white/20 text-white"
                          : "bg-navy text-white"
                      }
                    >
                      {trainer.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <p className="font-medium text-sm">
                      {trainer.name} {trainer.surname?.charAt(0)}.
                    </p>
                    <p
                      className={`text-xs ${
                        selectedTrainer?.trainer_id === trainer.trainer_id
                          ? "text-white/70"
                          : "text-grey"
                      }`}
                    >
                      {getLocalizedBranch(trainer.branches?.[0])}
                    </p>
                  </div>
                </button>
              ))
            ) : (
              <div className="col-span-2 text-center py-4 text-grey">
                Henüz antrenör eklenmemiş
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Time Slots */}
      {selectedTrainer && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Saat Seçin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {TIME_SLOTS.map((time) => {
                // Check if any room is available for this trainer at this time
                let isAvailable = false;
                let hasGroupSlot = false;
                let groupRemaining = 0;

                rooms.forEach((room) => {
                  const status = getSlotStatus(time, selectedTrainer.trainer_id, room.room_id);
                  if (status.status === "available") isAvailable = true;
                  if (status.status === "group") {
                    hasGroupSlot = true;
                    groupRemaining = Math.max(groupRemaining, status.remaining);
                  }
                });

                const canBook = isAvailable || hasGroupSlot;

                return (
                  <button
                    key={time}
                    onClick={() => canBook && handleTimeSelect(time)}
                    disabled={!canBook}
                    className={`p-3 rounded-xl text-center transition-all relative ${
                      !canBook
                        ? "bg-grey-100 text-grey-400 cursor-not-allowed"
                        : hasGroupSlot
                        ? "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
                        : "bg-white text-navy hover:bg-navy hover:text-white border border-grey-200"
                    }`}
                    data-testid={`time-${time}`}
                  >
                    <span className="font-semibold">{time}</span>
                    {hasGroupSlot && (
                      <span className="block text-xs mt-1">
                        {groupRemaining} kişilik
                      </span>
                    )}
                    {!canBook && (
                      <span className="block text-xs mt-1">{t('reservations:full')}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-white border border-grey-200" />
                <span className="text-grey">{t('reservations:available')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-50 border border-green-200" />
                <span className="text-grey">{t('trainers:groupLesson')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-grey-100" />
                <span className="text-grey">{t('reservations:full')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Room Status Overview */}
      {selectedTrainer && rooms.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Oda Durumu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rooms.map((room) => {
                const roomReservations = reservations.filter(
                  (r) => r.room_id === room.room_id && ["pending", "confirmed"].includes(r.status)
                );
                const occupancy = Math.round((roomReservations.length / TIME_SLOTS.length) * 100);

                return (
                  <div
                    key={room.room_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                  >
                    <div>
                      <p className="font-medium text-sm">{room.name}</p>
                      <p className="text-xs text-grey">
                        Kapasite: {room.capacity} kişi
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-grey-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              occupancy > 80
                                ? "bg-red-500"
                                : occupancy > 50
                                ? "bg-yellow-500"
                                : "bg-green-500"
                            }`}
                            style={{ width: `${occupancy}%` }}
                          />
                        </div>
                        <span className="text-xs text-grey w-10">%{occupancy}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>{t('reservations:reservationConfirmation')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 p-4 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-grey">{t('common:date')}</span>
                <span className="font-medium">
                  {format(selectedDate, "dd/MM/yyyy, EEEE", { locale: getDateLocale() })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-grey">{t('common:time')}</span>
                <span className="font-medium">{selectedTime}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-grey">{t('common:trainer')}</span>
                <span className="font-medium">
                  {selectedTrainer?.name} {selectedTrainer?.surname}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-grey">{t('common:room')}</span>
                <span className="font-medium">{selectedRoom?.name}</span>
              </div>
            </div>

            <p className="text-sm text-grey text-center">
              Rezervasyonunuz onay için işletmeye gönderilecektir.
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowConfirmModal(false)}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleConfirmReservation}
                disabled={submitting}
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Onayla
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
